import type { Data } from '@generated/data'
import { motion } from 'motion/react'
import type { LucideIcon } from 'lucide-react'
import { ArrowRight, Book, Calendar, Clock, Tag, User } from 'lucide-react'
import { Seo } from '~/components/Seo'
import { urlFor } from '~/core/tuyau'
import { WebsiteLayout } from '~/lib/components/layouts/WebsiteLayout'
import { Section } from '~/lib/components/website/Section'
import { fadeInStagger } from '~/lib/utils/animations'

type BlogCategory = {
  icon: LucideIcon
  title: string
  description: string
}

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })

const categories: BlogCategory[] = [
  {
    icon: Book,
    title: 'Literature Reviews',
    description: 'Best practices and innovations in medical device literature reviews',
  },
  {
    icon: Clock,
    title: 'Regulatory Updates',
    description: 'Latest MDR requirements and regulatory compliance guidance',
  },
  {
    icon: Tag,
    title: 'Industry Insights',
    description: 'Expert perspectives on medical device development',
  },
]

const BlogCard = ({ post, index }: { post: Data.Website.BlogPost; index: number }) => (
  <motion.div
    variants={fadeInStagger(index * 0.1)}
    viewport={{ once: true }}
    className="group relative overflow-hidden rounded-xl bg-white/10 p-1 backdrop-blur-xs"
  >
    <div className="relative overflow-hidden rounded-lg bg-white">
      <div className="p-6">
        <div className="mb-4 flex items-center gap-4 text-sm text-gray-600">
          <span className="flex items-center gap-1">
            <Calendar className="size-4" />
            <time dateTime={post.date}>{formatDate(post.date)}</time>
          </span>
          <span>•</span>
          <span className="flex items-center gap-1">
            <Clock className="size-4" />
            {post.readTime}
          </span>
        </div>
        <h3 className="mb-4 text-xl font-bold text-[#001871] transition-colors group-hover:text-primary">
          {post.title}
        </h3>
        <p className="mb-6 text-gray-600">{post.excerpt}</p>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <User className="size-4" />
            <span>By {post.author.name}</span>
          </div>
          <a
            href={urlFor('website.blog.post', { slug: post.slug })}
            className="inline-flex items-center gap-1 font-medium text-primary transition-colors hover:text-primary/80"
          >
            Read More
            <ArrowRight className="size-4" />
          </a>
        </div>
      </div>
    </div>
  </motion.div>
)

const CategoryCard = ({ category, index }: { category: BlogCategory; index: number }) => (
  <motion.div
    variants={fadeInStagger(index * 0.1)}
    viewport={{ once: true }}
    className="flex flex-col items-start rounded-xl bg-white/10 p-6 shadow-lg backdrop-blur-xs"
  >
    <div className="mb-4 rounded-xl bg-white/20 p-3">
      <category.icon className="size-6 text-white" />
    </div>
    <h3 className="mb-2 text-lg font-semibold text-white">{category.title}</h3>
    <p className="text-sm text-white/70">{category.description}</p>
  </motion.div>
)

export default function Blog({ posts }: { posts: Data.Website.BlogPost[] }) {
  return (
    <>
      <Seo
        title="Clinical Evaluation and Literature Review Guides | SOTA Navigator"
        description="Practical writing on literature search protocols, screening, appraisal and PRISMA reporting for medical device clinical evaluations under EU MDR."
      />
      <WebsiteLayout
        showHero
        heroContent={
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="mx-auto max-w-4xl text-center">
              <motion.div
                initial="initial"
                animate="animate"
                variants={fadeInStagger()}
                className="space-y-8"
              >
                <motion.div
                  variants={fadeInStagger()}
                  className="inline-flex items-center rounded-full bg-white/20 px-6 py-2 text-sm font-medium text-white shadow-lg backdrop-blur-xs"
                >
                  Latest Updates & Insights
                </motion.div>

                <motion.h1
                  variants={fadeInStagger(0.2)}
                  className="text-5xl font-bold tracking-tight text-white [text-shadow:0_1px_2px_rgb(0_0_0/20%)] sm:text-7xl"
                >
                  Expert Knowledge Hub
                </motion.h1>
                <motion.p
                  variants={fadeInStagger(0.3)}
                  className="mx-auto max-w-3xl text-xl text-white/80"
                >
                  Stay informed with the latest insights in medical device development and
                  regulatory compliance
                </motion.p>

                <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                  {categories.map((category, index) => (
                    <CategoryCard key={category.title} category={category} index={index} />
                  ))}
                </div>
              </motion.div>
            </div>
          </div>
        }
      >
        <Section>
          <motion.div
            initial="initial"
            animate="animate"
            variants={fadeInStagger()}
            className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3"
          >
            {posts.map((post, index) => (
              <BlogCard key={post.slug} post={post} index={index} />
            ))}
          </motion.div>
        </Section>
      </WebsiteLayout>
    </>
  )
}
